import { Injectable } from "@angular/core";
import { State, Selector, Action, StateContext } from "@ngxs/store";
import { Observable, tap } from "rxjs";
import { Picklist, RTAOptions, RawFeature } from "../../interfaces/request.interface";
import { RTAApiClient } from "../../services/rta-api.service";
import { OptionActions } from "../action/options.action";
import { AvailableCutter, Chamfer, CutterGroup, CutterMap } from "../../interfaces/cutterMap";
import { groupBy, GroupResult, GroupDescriptor, process, DataResult, SortDescriptor, orderBy } from "@progress/kendo-data-query";
import { BomPartTypeLayout } from "../../interfaces/request";

export interface OptionsStateModel {
    options: RTAOptions[],
    availableCutters: AvailableCutter[],
    cutterGroups: CutterGroup[],
    picklist: Picklist[],
    productLines: any[],
    productLines_groupedData: GroupResult[],
    activeChallenge: any[],
    rtaType: any[],
    priorities: any[],
    subApplication: any[],
    subApplications_groupedData: any[],
    electronics_Data: any[],
    uppersections_Data: any[],
    powderBinderData: any[],
    cerebroInfluenceTypes: any[],
    bitFeature: RawFeature[],
    standardPowderData: any[],
    onHoldCategories: any[],
    bomLaytout: BomPartTypeLayout[],
    bitBreakers: any[],
    fieldRegex: any[],
    gaugePadProtectionOptions: any[]

}

@State<OptionsStateModel>({
    name: 'options',
    defaults: {
        options: [] as RTAOptions[],
        availableCutters: [] as AvailableCutter[],
        cutterGroups: [] as CutterGroup[],
        picklist: [] as Picklist[],
        productLines: [] as any[],
        productLines_groupedData: [] as GroupResult[],
        activeChallenge: [] as any[],
        rtaType: [] as any[],
        priorities: [] as any[],
        subApplication: [] as any[],
        subApplications_groupedData: [] as any[],
        electronics_Data: [] as any[],
        uppersections_Data: [] as any[],
        powderBinderData: [] as any[],
        standardPowderData: [] as any[],
        bitFeature: [] as RawFeature[],
        cerebroInfluenceTypes: [] as any[],
        onHoldCategories: [] as any[],
        bomLaytout: [] as BomPartTypeLayout[],
        bitBreakers: [] as any[],
        fieldRegex: [] as any[],
        gaugePadProtectionOptions: [] as any[]
    }
})
@Injectable()
export class OptionState {
    constructor(private service: RTAApiClient) { }

    @Selector()
    static options(state: OptionsStateModel): RTAOptions[] {
        return state.options;
    }
    @Selector()
    static productlines(state: OptionsStateModel): any[] {
        return state.productLines;
    }
    @Selector()
    static productLines_groupedData(state: OptionsStateModel): GroupResult[] {
        return state.productLines_groupedData;
    }
    @Selector()
    static subApplication(state: OptionsStateModel): any[] {
        return state.subApplication;
    }
    @Selector()
    static subApplications_groupedData(state: OptionsStateModel): GroupResult[] {
        return state.subApplications_groupedData;
    }
    @Selector()
    static activeChallenge(state: OptionsStateModel): any[] {
        return state.activeChallenge;
    }
    @Selector()
    static rtaType(state: OptionsStateModel): any[] {
        return state.rtaType;
    }
    @Selector()
    static rtaFilteredType(state: OptionsStateModel): any[] {
        return state.rtaType;
    }
    @Selector()
    static priorities(state: OptionsStateModel): any[] {
        return state.priorities;
    }
    @Selector()
    static availableCutters(state: OptionsStateModel): AvailableCutter[] {
        return state.availableCutters;
    }
    @Selector()
    static electronics(state: OptionsStateModel): any[] {
        return state.electronics_Data;
    }
    @Selector()
    static uppersection(state: OptionsStateModel): any[] {
        return state.uppersections_Data;
    }
    @Selector()
    static bodyMaterial(state: OptionsStateModel): any[] {
        return state.powderBinderData;
    }
    @Selector()
    static PowderStandards(state: OptionsStateModel): any[] {
        return state.standardPowderData;
    }
    @Selector()
    static bitFeature(state: OptionsStateModel): RawFeature[] {
        return state.bitFeature;
    }
    @Selector()
    static cerebroInfluenceTypes(state: OptionsStateModel): any[] {
        return state.cerebroInfluenceTypes;
    }
    @Selector()
    static onHoldCategories(state: OptionsStateModel): any[] {
        return state.onHoldCategories;
    }

    @Selector()
    static getBomLayout(state: OptionsStateModel): BomPartTypeLayout[] {
        return state.bomLaytout;
    }
    @Selector()
    static bitBreakers(state: OptionsStateModel): any[] {
        return state.bitBreakers;
    }
    @Selector()
    static fieldRegex(state: OptionsStateModel): any[] {
        return state.fieldRegex;
    }
    @Selector()
    static gaugePadProtectionOptions(state: OptionsStateModel): any[] {
        return state.gaugePadProtectionOptions;
    }
    @Selector([OptionState.availableCutters])
    static cutterChamfers(allCutters: AvailableCutter[]): Chamfer[] {
        const statusMap = new Map<string, boolean>();

        for (const c of allCutters) {
            const ch = c.cutterChamfer;
            if (!ch) { continue; }
            if (!statusMap.has(ch)) {
                statusMap.set(ch, true);
            }
            if (c.isObsolete === false) {
                statusMap.set(ch, false);
            }
        }

        return Array.from(statusMap.entries()).sort(([aChamfer], [bChamfer]) =>
            aChamfer.localeCompare(bChamfer)
        ).map(([cutterChamfer, isObsolete]) => ({
            cutterChamfer,
            isObsolete
        }));
    }
    @Selector()
    static cutterGroups(state: OptionsStateModel): CutterGroup[] {
        return state.cutterGroups;
    }
    @Selector()
    static picklist(state: OptionsStateModel): Picklist[] {
        return state.picklist;
    }
    @Action(OptionActions.GetOptions)
    getRTAOptions(ctx: StateContext<OptionsStateModel>, action: OptionActions.GetOptions): Observable<RTAOptions[]> {

        return this.service.retreiveOrderOptions(action.payload.id).pipe(tap(options => {
            options.forEach(element => {
                switch (element.field) {
                    case 'Productline': {
                        const ProductLinesData: any[] = [];
                        const ProductLines = this.parseOptionsArray(element.fieldOptions);
                        ProductLines.forEach((data: any) => {
                            ProductLinesData.push({
                                value: data.value,
                                name: data.name,
                                category: 'SubPSL',
                                subcategory: data.subcat,
                                permissionID: data.PermissionId
                            });
                        });
                        ctx.patchState({
                            productLines: ProductLinesData, productLines_groupedData: groupBy(ProductLinesData, [
                                { field: 'subcategory' }
                            ])
                        });
                        break;
                    }

                    case 'ActiveChallenge': {
                        const _activeProblems: any[] = [];
                        const ActiveProblems = this.parseOptionsArray(element.fieldOptions);
                        ActiveProblems.forEach((data: any) => {
                            _activeProblems.push({ id: data.value, name: data.name });
                        });
                        ctx.patchState({ activeChallenge: _activeProblems });
                        break;
                    }

                    case 'RTAType': {
                        const TypesData: any[] = [];
                        const Types = this.parseOptionsArray(element.fieldOptions);
                        Types.forEach((data: any) => {
                            TypesData.push({
                                value: data.value,
                                name: data.name,
                                permissionid: data.subcat
                            });
                        });
                        ctx.patchState({ rtaType: TypesData });
                        break;
                    }

                    case 'Priority': {
                        const PriorityData: any[] = [];
                        const Priorities = this.parseOptionsArray(element.fieldOptions);
                        Priorities.forEach((data: any) => {
                            PriorityData.push({
                                value: data.value,
                                name: data.name
                            });
                        });
                        ctx.patchState({ priorities: PriorityData });
                        break;
                    }
                    case 'SubApplication': {
                        const SubApplicationData: any[] = [];
                        const SubApplications = this.parseOptionsArray(element.fieldOptions);
                        SubApplications.forEach((data: any) => {
                            SubApplicationData.push({
                                value: data.value,
                                name: data.name,
                                category: 'Application',
                                subcategory: data.subcat,
                            });
                        });
                        ctx.patchState({
                            subApplication: SubApplicationData, subApplications_groupedData: groupBy(SubApplicationData, [
                                { field: 'subcategory' }
                            ])
                        });
                        break;
                    }
                    case 'Electronics': {
                        const _ElectronicsData = this.parseOptionsArray(element.fieldOptions);
                        ctx.patchState({ electronics_Data: _ElectronicsData });
                        break;
                    }
                    case 'Uppersection': {
                        const _UppersectionData = this.parseOptionsArray(element.fieldOptions);
                        ctx.patchState({ uppersections_Data: _UppersectionData });
                        break;
                    }
                    case 'BodyMaterial': {
                        const _PowderBinderData = this.parseOptionsArray(element.fieldOptions);
                        ctx.patchState({ powderBinderData: _PowderBinderData });
                        break;
                    }
                    case 'PowderStandards': {
                        const PowderStandardsData: any[] = [];
                        const _PowderStandards = this.parseOptionsArray(element.fieldOptions);
                        _PowderStandards.forEach((data: any) => {
                            PowderStandardsData.push({
                                value: data.Name,
                                name: data.Name,
                                face: data.FacePowder,
                                gauge: data.GaugePowder,
                                machinable: data.MachinablePowder,
                                binder: data.Binder,
                                SizeMin: data.SizeMin,
                                SizeMax: data.SizeMax
                            });
                        });
                        ctx.patchState({ standardPowderData: PowderStandardsData });
                        break;
                    }
                    case 'BitFeature': {
                        const _BitFeatures: RawFeature[] = this.parseOptionsArray(element.fieldOptions);
                        const group: GroupDescriptor[] = [{ field: 'subcat', dir: 'asc' }];
                        ctx.patchState({ bitFeature: process(_BitFeatures, { group: group }).data });
                        break;
                    }
                    case 'OnHoldCategories': {
                        const _onHoldCategories = this.parseOptionsArray(element.fieldOptions);
                        ctx.patchState({ onHoldCategories: _onHoldCategories });
                        break;
                    }
                    case 'BitBreaker': {
                        const _bitBreakers = this.parseOptionsArray(element.fieldOptions);
                        ctx.patchState({ bitBreakers: _bitBreakers });
                        break;
                    }
                    case 'FieldRegex': {
                        const _fieldRegexes = this.parseOptionsArray(element.fieldOptions);
                        ctx.patchState({ fieldRegex: _fieldRegexes });
                        break;
                    }
                    case 'GaugePadProtection': {
                        const GaugePadProtectionData: any[] = [];
                        const _GaugePadProtectionOptions = this.parseOptionsArray(element.fieldOptions);
                        _GaugePadProtectionOptions.forEach((data: any) => {
                            GaugePadProtectionData.push({
                                value: data.value,
                                name: data.Name,
                                isObsolete: data.isObsolete,
                                productline_id: data.Productline_Id
                            });
                        });
                        ctx.patchState({ gaugePadProtectionOptions: GaugePadProtectionData });
                        break;
                    }
                    case 'CerebroInfluenceTypes': {
                        const sort: SortDescriptor[] = [{
                            field: 'rank',
                            dir: 'asc'
                        }];
                        const CerebroInfluenceTypeData: any[] = [];
                        const _CerebroInfluenceTypes = this.parseOptionsArray(element.fieldOptions);
                        _CerebroInfluenceTypes.forEach((data: any) => {
                            CerebroInfluenceTypeData.push({
                                value: data.value,
                                name: data.Name,
                                rank: data.rank
                            });
                        });

                        ctx.patchState({ cerebroInfluenceTypes: orderBy(CerebroInfluenceTypeData, sort) });
                        break;
                    }
                }
            });
        }));
    }

    private parseOptionsArray(fieldOptions?: string | null): any[] {
        if (!fieldOptions) {
            return [];
        }
        try {
            const parsed = JSON.parse(fieldOptions);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    @Action(OptionActions.GetAvailableCutters)
    getAvailableCutters(ctx: StateContext<OptionsStateModel>, action: OptionActions.GetAvailableCutters): Observable<AvailableCutter[]> {

        return this.service.getAvailableCutters(action.payload.id, action.payload.sapnumber ?? 0).pipe(tap(availableCutters => { ctx.patchState({ availableCutters }) }));
    }
    @Action(OptionActions.GetCutterGroupByOrder)
    getCutterGroupByOrder(ctx: StateContext<OptionsStateModel>, action: OptionActions.GetCutterGroupByOrder): Observable<CutterGroup[]> {

        return this.service.getCutterGroupByOrder(action.payload.id).pipe(tap(cutterGroups => { ctx.patchState({ cutterGroups }) }));
    }
    @Action(OptionActions.GetPicklist)
    getPicklist(ctx: StateContext<OptionsStateModel>): Observable<Picklist[]> {
        return this.service.getPickList().pipe(tap(picklist => {
            ctx.patchState({ picklist })
        }));
    }
    @Action(OptionActions.GetBomLayout)
    getBomLayout(ctx: StateContext<OptionsStateModel>, action: OptionActions.GetBomLayout): Observable<BomPartTypeLayout[]> {
        return this.service.getBomLayout(action.payload.id).pipe(
            tap(bomLayout => {
                if (bomLayout) {
                    ctx.patchState({ bomLaytout: bomLayout });
                }
            })
        );
    }
}
